import { observer } from 'mobx-react-lite';
import { defineMessages, useIntl } from 'react-intl';
import { cn } from '@/app/lib/utils';
import { currentStudioPhase, STUDIO_PHASES } from './studio-state.ts';

const messages = defineMessages({
  understand: { id: 'sayMyName.phase.understand', defaultMessage: 'UNDERSTAND', description: 'First phase of the naming process: learning what is being named' },
  discover: { id: 'sayMyName.phase.discover', defaultMessage: 'DISCOVER', description: 'Second phase: exploring territory and researching candidates' },
  decide: { id: 'sayMyName.phase.decide', defaultMessage: 'DECIDE', description: 'Third phase: comparing finalists and recommending' },
  label: { id: 'sayMyName.phase.label', defaultMessage: 'Where we are', description: 'Accessible label for the three-phase process indicator' },
});

/** Same shared observable; slightly smaller type distinguishes progress from agent identity.
 * One unbroken flex item lets all three titles wrap together beneath the label. */
export const PhaseIndicator = observer(function PhaseIndicator() {
  const intl = useIntl();
  const active = currentStudioPhase();
  return (
    <p className="flex flex-none items-center gap-3 whitespace-nowrap sm:gap-4" aria-label={intl.formatMessage(messages.label)}>
      {STUDIO_PHASES.map((phase) => (
        <span key={phase} aria-current={phase === active ? 'step' : undefined}
          className={cn('text-[8px] font-medium leading-none tracking-caps transition-colors duration-300 sm:text-[9px]',
            phase === active ? 'text-foreground' : 'text-muted-foreground-subtle')}>
          {intl.formatMessage(messages[phase])}
        </span>
      ))}
    </p>
  );
});

/** Invisible, noninteractive sizing placeholder only — no phase state or active
 * styling. Preserves the former header footprint exactly (including localized
 * label widths and mobile wrapping) so ORIGIN does not shift. */
export function PhaseHeaderSpace() {
  const intl = useIntl();
  return (
    <div aria-hidden="true" className="invisible pointer-events-none ml-auto flex flex-none items-center justify-end gap-3 sm:ml-0 sm:gap-4 lg:w-[calc(32vw-5rem)]">
      {STUDIO_PHASES.map((phase) => (
        <span key={phase} className="border-b border-transparent pb-0.5 text-[9px] font-medium leading-none tracking-caps sm:text-[10px]">
          {intl.formatMessage(messages[phase])}
        </span>
      ))}
    </div>
  );
}
